import { first, all } from "./util"

type AddToCartResult = {
    productId: number
    variantName: string
    amount: number
    error: string
}

let canFetch = true;

const isMobileView = () => {
    return window.innerWidth <= 1000;
}

function getCartAnimationContainer<T extends HTMLElement | SVGElement = HTMLElement>() {
    const animationContainer = isMobileView() ? first<T>(".mobile-nav") : first<T>(".cart-text");
    if (!animationContainer) {
        throw new Error('no animation container found');
    } else {
        return animationContainer;
    }
}

const randomIntFromRange = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1) + min)
}

const getAddToCartSections = () => {
    return all(".product-components__add-to-cart__section");
}

class BoxAnimation {
    
    private readonly img: HTMLImageElement
    private readonly animationTarget: HTMLElement

    constructor() {
        this.img = document.createElement("img");
        this.img.setAttribute("src", "/img/cart/bg-effect.png");
        this.img.setAttribute("class", "cart-text__animation-img");
        this.animationTarget = getCartAnimationContainer();
    }

    playAnimation() {
        this.animationTarget.appendChild(this.img);

        setTimeout(() => {
            this.img.classList.add(isMobileView() ? "fly-down" : "fly-up");
            const x = randomIntFromRange(1, 80) * (randomIntFromRange(1, 10) < 5 ? 1 : -1);
            this.img.style.transform = `rotate(${x}deg)`;
        }, 10);

        setTimeout(() => {
            this.animationTarget.removeChild(this.img);
        }, 2000);
    }
}

const playAddToCartAnimation = () => {
    const animation = new BoxAnimation();
    animation.playAnimation();
}

const variantOk = (result: AddToCartResult, container: HTMLElement) => {
    return container.getAttribute("data-variant") === result.variantName;
}

const productIdOk = (result: AddToCartResult, container: HTMLElement) => {
    return container.getAttribute("data-product-id") === result.productId.toString()
}

const updateButtonAmount = (result: AddToCartResult) => {

    const existingItemElements = all<HTMLElement>(".btn--add-to-cart__existing-items");

    for (const existingItemsElement of existingItemElements) {
        if (variantOk(result, existingItemsElement) && productIdOk(result, existingItemsElement)) {
            var buttonText = existingItemsElement.innerText;
            if (buttonText) {
                const before = parseInt(existingItemsElement.innerText, 10);
                const after = before + result.amount;
                existingItemsElement.innerText = after.toString();
            } else {
                existingItemsElement.innerText = result.amount.toString();
            }
        }
    }
}

const updateNavCartText = (result: AddToCartResult) => {
    const e = first(".cart-text");
    const existingNode = e.firstChild;

    if (existingNode) {
        const existingText = existingNode.nodeValue;

        if (existingText) {
            const leftBracketPosition = existingText.indexOf("(");
            const rightBracketPosition = existingText.indexOf(")");

            if (leftBracketPosition === -1) {
                existingNode.nodeValue = `Einkaufswagen (${result.amount})`;
            } else {
                const amountAsText = existingText.substring(leftBracketPosition + 1, rightBracketPosition);
                const existingAmount = parseInt(amountAsText);
                const newAmount = existingAmount + result.amount;
                existingNode.nodeValue = `Einkaufswagen (${newAmount})`;
            }
        }
    }
}

class ButtonAnimation {

    private readonly btn: HTMLElement

    constructor(btn: HTMLElement) {
        this.btn = btn;
    }

    playAnimation() {
        const div = first(".btn--add-to-cart__existing-items", this.btn);

        if (!div.classList.contains('bump')) {
            div.classList.add('bump');
        }

        setTimeout(() => {
            if (div.classList.contains('bump')) {
                div.classList.remove('bump');
            }
        }, 500);
    }
}

const playButtonAnimation = (btn: HTMLElement) => {
    var a = new ButtonAnimation(btn);
    a.playAnimation();
}

const clearPrevErrors = () => {
    for (const div of all(".errors")) {
        if (div.parentElement) {
            div.parentElement.removeChild(div);
        }
    }
}

const getContainer = (error: AddToCartResult) => {
    for (const container of all<HTMLElement>('.hover-content')) {
        if (container.getAttribute("data-variant") === error.variantName && container.getAttribute("data-product-id") === error.productId.toString()) {
            return container;
        }
    }
}

const createHoverElement = (error: AddToCartResult) => {
    // find the hover content element
    var container = getContainer(error);

    // put the error message in there, give it the 'hover effect' class
    if (container) {
        if (!container.innerText) {
            container.innerText = error.error;
        }
        if (!container.classList.contains('btn--add-to-cart__hover-content')) {
            container.classList.add('btn--add-to-cart__hover-content');
        }
    }
}

const disableButton = (result: AddToCartResult, state: boolean) => {
    // find the hover content element
    var container = getContainer(result);

    // ..which is a child of the button
    if (container) {
        const btn = container.parentElement;

        if (btn instanceof HTMLButtonElement) {
            btn.disabled = state;

            // gray out the image also
            if (state) {
                var img = btn.firstChild;
                if (img && img instanceof HTMLImageElement && !img.classList.contains('btn--add-to-cart__image-gray')) {
                    img.classList.add('btn--add-to-cart__image-gray');
                }
            }
        }
    }
}

const addToCartEventListener = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (canFetch && e?.currentTarget instanceof Element) {
        const form = first<HTMLFormElement>('form', e.currentTarget);
        const formData = new FormData(form);

        const button = first<HTMLElement>('button', form);

        if (button.hasAttribute('disabled')) {
            return;
        }

        button.setAttribute('disabled', 'true');

        canFetch = false;

        try {
            const response = await fetch("/Api/AddProduct", { method: 'POST', body: formData, credentials: 'include' })
            const result: AddToCartResult = await response.json();
            canFetch = true;
            clearPrevErrors();

            if (result.error) {
                throw result;
            } else {
                updateButtonAmount(result);
                updateNavCartText(result);
                playAddToCartAnimation();
                playButtonAnimation(button);
                disableButton(result, false);
            }
        } catch (result) {
            canFetch = true;
            if (result.error) {
                createHoverElement(result);
            }
            disableButton(result, true);
        }
    }
}

(() => {
    for (const element of getAddToCartSections()) {
        element.addEventListener('click', addToCartEventListener);
    }
})()